(function attachJudesSetCheckout(root, factory) {
  const api = factory(root);
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.JudesSetCheckout = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, function createJudesSetCheckout(root) {
  'use strict';

  function normalizeRoot(routesRoot) {
    const value = String(routesRoot || '/');
    return value.endsWith('/') ? value : `${value}/`;
  }

  async function responseJson(response) {
    return response.json().catch(() => ({}));
  }

  function syncThemeCart(cart) {
    try {
      if (root.CartJS?.cart && typeof root.CartJS.cart.update === 'function') root.CartJS.cart.update(cart);
      const counter = root.document?.getElementById?.('counter');
      if (counter && Number.isSafeInteger(cart?.item_count)) counter.setAttribute('data-count', String(cart.item_count));
    } catch (error) {
      // Checkout correctness must not depend on an optional theme-cart renderer.
    }
  }

  function unsafeCleanupError() {
    const error = new Error('The prepared set could not be safely restored. Please review your cart');
    error.partialCart = true;
    return error;
  }

  async function restoreCartAtomically(fetchFn, routesRoot, current, before, beforeFingerprint, submission) {
    const owned = (cart) => cart.items.filter((item) => item.properties?._judes_set_submission === submission);
    const untouched = (cart) => cart.items.filter((item) => item.properties?._judes_set_submission !== submission);
    let currentOwned;
    try {
      if (!current || !before || !Array.isArray(current.items) || !Array.isArray(before.items)
        || !Array.isArray(before.discount_codes) || current.currency !== before.currency
        || root.JudesSetCommerce.basketKey(before) !== beforeFingerprint
        || root.JudesSetCommerce.identity(untouched(current)) !== root.JudesSetCommerce.identity(before.items)) {
        throw unsafeCleanupError();
      }
      currentOwned = owned(current);
    } catch (error) {
      if (error.partialCart) throw error;
      throw unsafeCleanupError();
    }
    if (!currentOwned.length) throw unsafeCleanupError();
    const keys = [...new Set(currentOwned.map((item) => item.key))];
    if (keys.some((key) => typeof key !== 'string' || !key)
      || untouched(current).some((item) => keys.includes(item.key))) throw unsafeCleanupError();
    const updates = Object.fromEntries(keys.map((key) => [key, 0]));
    const discount = before.discount_codes.map((item) => item.code).join(',');

    let candidate = null;
    try {
      const response = await fetchFn(`${routesRoot}cart/update.js`, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates, discount })
      });
      candidate = await responseJson(response);
      if (!response.ok) candidate = null;
    } catch (error) {
      candidate = null;
    }
    const exact = (cart) => {
      try {
        return cart && Array.isArray(cart.items) && owned(cart).length === 0
          && root.JudesSetCommerce.basketKey(cart) === beforeFingerprint;
      } catch (error) {
        return false;
      }
    };
    if (exact(candidate)) return candidate;

    try {
      const read = await fetchFn(`${routesRoot}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
      const confirmed = await responseJson(read);
      if (read.ok && exact(confirmed)) return confirmed;
    } catch (error) {
      // The single write remains ambiguous; no additional mutation is safe.
    }
    throw unsafeCleanupError();
  }

  function normalizeDiscountCodes(value) {
    const parts = String(value || '').split(',').map(code => code.trim());
    if (!parts.every(code => /^[a-z0-9_-]+$/i.test(code))) throw new Error('The set discount is not configured yet');
    const codes = parts.filter((code, index) => parts.findIndex(other => other.toLowerCase() === code.toLowerCase()) === index);
    if (codes.length > 5) throw new Error('Too many set discounts');
    return codes;
  }

  function discountsAreApplicable(cart, codes) {
    return Array.isArray(cart.discount_codes) && codes.every(code =>
      cart.discount_codes.some(discount => String(discount?.code || '').toLowerCase() === code.toLowerCase()
        && discount.applicable === true));
  }

  function selectionChangedError() {
    const error = new Error('The selection changed, please review the new total');
    error.checkoutCanceled = true;
    return error;
  }

  function verifyBasket(cart, payload, verification, before, submission) {
    if (!discountsAreApplicable(cart, normalizeDiscountCodes(verification.code))) throw new Error('The set discount could not be confirmed');
    if (!Array.isArray(cart.items)) throw new Error('The set basket could not be confirmed');
    const lines = cart.items.filter((item) => item.properties?._judes_set_submission === submission);
    const quantities = (items) => {
      const result = {};
      for (const item of items) {
        const id = String(item.variant_id || item.id);
        if (!Number.isInteger(Number(item.quantity)) || Number(item.quantity) <= 0) throw new Error('Invalid set basket quantity');
        result[id] = (result[id] || 0) + Number(item.quantity);
      }
      return JSON.stringify(Object.entries(result).sort());
    };
    if (quantities(lines) !== quantities(payload.items)) throw new Error('The set basket does not match your selection');
    if (root.JudesSetCommerce.identity(lines) !== root.JudesSetCommerce.identity(payload.items)) throw new Error('The set basket does not match your selection');
    const retail = lines.reduce((sum, item) => sum + item.original_line_price, 0);
    const untouched = cart.items.filter((item) => item.properties?._judes_set_submission !== submission);
    const previous = before.items || [];
    if (quantities(untouched) !== quantities(previous)
      || (verification.beforeFingerprint && root.JudesSetCommerce.identity(untouched) !== root.JudesSetCommerce.identity(previous))) {
      throw new Error('The existing basket changed during checkout');
    }
    if (retail !== verification.retailValueMinor
      || cart.total_price !== (verification.wholeCartPriceMinor ?? before.total_price + verification.finalPriceMinor)
      || (verification.currency && cart.currency !== verification.currency)) throw new Error('The set price or discount does not match your selection');
    return true;
  }

  async function stage(options) {
    const fetchFn = options.fetchFn;
    const routesRoot = normalizeRoot(options.routesRoot);
    const discountCodes = normalizeDiscountCodes(options.discountCode);
    const discountCode = discountCodes.join(',');
    if (!discountCode) throw new Error('The set discount is not configured yet');
    if (!options.cartPayload || !Array.isArray(options.cartPayload.items) || options.cartPayload.items.length === 0) throw new Error('The bundle cart is empty');
    if (!options.verification || !options.verification.beforeFingerprint || !Number.isSafeInteger(options.verification.wholeCartPriceMinor)) throw new Error('Missing native cart verification');

    let before;
    let submission;
    let cartPayload = options.cartPayload;
    const v = options.verification;
    if (![v.retailValueMinor, v.finalPriceMinor, v.discountAmountMinor].every((value) => Number.isInteger(value) && value >= 0)
      || v.retailValueMinor - v.discountAmountMinor !== v.finalPriceMinor) throw new Error('Invalid set price verification');
    if (v.beforeCart) {
      before = v.beforeCart;
    } else {
      const beforeResponse = await fetchFn(`${routesRoot}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
      before = await responseJson(beforeResponse);
      if (!beforeResponse.ok) throw new Error('The existing basket could not be confirmed');
    }
    if (!before || !Array.isArray(before.items) || !Number.isInteger(before.total_price)) throw new Error('The existing basket could not be confirmed');
    if (v.beforeFingerprint && root.JudesSetCommerce.basketKey(before) !== v.beforeFingerprint) throw new Error('The cart changed, please review the new total');
    submission = `set-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    cartPayload = { ...cartPayload, items: cartPayload.items.map((item) => ({ ...item,
      properties: { ...(item.properties || {}), _judes_set_submission: submission }
    })) };

    if (options.isCurrent && !options.isCurrent()) throw selectionChangedError();
    const cartResponse = await fetchFn(`${routesRoot}cart/add.js`, {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cartPayload)
    });
    const added = await responseJson(cartResponse);
    if (!cartResponse.ok) throw new Error(added.description || 'The set could not be added to your cart');

    try {
      const discountResponse = await fetchFn(`${routesRoot}cart/update.js`, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ discount: discountCode })
      });
      const cart = await responseJson(discountResponse);
      if (!discountResponse.ok || !discountsAreApplicable(cart, discountCodes)) throw new Error('The set discount could not be applied');
      const verified = verifyBasket(cart, cartPayload, { ...v, code: discountCode }, before, submission);
      if (options.isCurrent && !options.isCurrent()) throw selectionChangedError();
      syncThemeCart(cart);
      return {
        added,
        cart,
        verified,
        submission,
        beforeCart: before,
        beforeFingerprint: root.JudesSetCommerce.basketKey(before),
        afterFingerprint: root.JudesSetCommerce.basketKey(cart),
        stagedAt: Date.now()
      };
    } catch (error) {
      let rollbackCart = null;
      if (v.beforeFingerprint) {
        try {
          const response = await fetchFn(`${routesRoot}cart.js`, {credentials:'same-origin',cache:'no-store'});
          const current = await responseJson(response);
          if (!response.ok || !Array.isArray(current.items)) throw Error('Cart unavailable');
          rollbackCart = current;
        } catch (readError) { rollbackCart = null; }
      }
      let restoredCart = null;
      if (rollbackCart) {
        try {
          restoredCart = await restoreCartAtomically(fetchFn, routesRoot, rollbackCart, before, v.beforeFingerprint, submission);
        } catch (restoreError) {
          error.partialCart = true;
        }
      } else error.partialCart = true;
      if (restoredCart) syncThemeCart(restoredCart);
      if (error.partialCart) {
        error.message = `${error.message}. Please review your cart before trying again`;
      }
      throw error;
    }
  }

  async function unstage(options) {
    const fetchFn = options.fetchFn;
    const routesRoot = normalizeRoot(options.routesRoot);
    const staged = options.staged;
    if (!staged || typeof staged.submission !== 'string' || !/^set-/.test(staged.submission)
      || !staged.beforeCart || !staged.beforeFingerprint || !staged.afterFingerprint) {
      throw new Error('The prepared set cannot be safely removed');
    }

    const response = await fetchFn(`${routesRoot}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
    const current = await responseJson(response);
    if (!response.ok || !Array.isArray(current.items)) throw new Error('The prepared cart could not be confirmed');
    if (root.JudesSetCommerce.basketKey(current) !== staged.afterFingerprint) {
      throw new Error('The cart changed before the prepared set could be removed');
    }

    const restored = await restoreCartAtomically(
      fetchFn, routesRoot, current, staged.beforeCart, staged.beforeFingerprint, staged.submission
    );
    syncThemeCart(restored);
    return { cart: restored, restored: true };
  }

  async function submit(options) {
    const staged = await stage(options);
    const ensureCurrent = async () => {
      if (!options.isCurrent || options.isCurrent()) return;
      try {
        await unstage({ routesRoot: options.routesRoot, staged, fetchFn: options.fetchFn });
      } catch (error) {
        error.unsafeCleanup = true;
        throw error;
      }
      throw selectionChangedError();
    };
    await ensureCurrent();
    if (typeof options.onSuccess === 'function') await options.onSuccess(staged);
    await ensureCurrent();
    options.navigate(`${normalizeRoot(options.routesRoot)}checkout`);
    return staged;
  }

  return { stage, unstage, submit };
}));
